import * as yup from "yup"

const MESSAGES = {
  lengthContinuity:
    "The min length must be equal to the max length of the previous rate",
  maxLengthGreaterThanMin: "Max length must be greater than min length",
  atLeastOneRateRequired: "at least 1 rate is required",
  atLeastOneMinLengthZero: "At least 1 min length must start at 0.",
  parentBeamStartsAtZero: "Parent rows must start with a min beam of 0.",
  beamContinuity: "Beam ranges within the same LOA group must be continuous.",
  maxBeamGreaterThanMin: "Max beam must be greater than min beam",
  allParentBeamsStartAtZero: "All parent rows must have beams starting at 0.",
  required: "This field is required.",
  numberMsg: "Amount must be a number.",
  min0Msg: "Amount must be greater than 0.",
  maxLengthMsg: "Length must be at most 2,000 feet.",
  maxBeamMsg: "Beam must be at most 1,000 feet.",
  nameMsg: "Name is required.",
  rateTypeMsg: "Rate type is required.",
}

export const schema = (getParentChildRelationships = () => ({})) => {
  const baseRateShape = {
    minLength: yup
      .number()
      .typeError(MESSAGES.numberMsg)
      .required(MESSAGES.required)
      .min(0, MESSAGES.min0Msg)
      .max(2000, MESSAGES.maxLengthMsg)
      .test(
        "length continuity",
        MESSAGES.lengthContinuity,
        function (value, context) {
          const index = parseInt(context.path.match(/\[(\d+)\]/)?.[1] || "0")

          // Use from[1].value.rates - this is the rateStrategy.rates array with current values
          const rates = context.from?.[1]?.value?.rates || []

          if (index > 0 && rates && rates.length > index) {
            const currentRate = rates[index]
            const previousRate = rates[index - 1]

            // If this looks like a beam tier (same LOA), skip continuity check
            if (currentRate && previousRate) {
              const isSameLOATier =
                parseInt(currentRate.minLength || 0) ===
                  parseInt(previousRate.minLength || 0) &&
                parseInt(currentRate.maxLength || 0) ===
                  parseInt(previousRate.maxLength || 0)

              if (isSameLOATier) {
                return true
              }
            }

            if (previousRate) {
              const expectedValue = parseInt(previousRate.maxLength || 0)
              const actualValue = parseInt(value || 0)
              return actualValue === expectedValue
            }
          }

          return true
        }
      ),
    maxLength: yup
      .number()
      .typeError(MESSAGES.numberMsg)
      .required(MESSAGES.required)
      .min(0, MESSAGES.min0Msg)
      .max(2000, MESSAGES.maxLengthMsg)
      .when("minLength", (minLength) => {
        if (minLength) {
          return yup
            .number()
            .typeError(MESSAGES.numberMsg)
            .min(minLength, MESSAGES.maxLengthGreaterThanMin)
            .max(2000, MESSAGES.maxLengthMsg)
        }
      }),
    rate: yup
      .number()
      .typeError(MESSAGES.numberMsg)
      .required(MESSAGES.required)
      .min(0, MESSAGES.min0Msg),
    minBeam: yup
      .number()
      .typeError(MESSAGES.numberMsg)
      .required(MESSAGES.required)
      .min(0, MESSAGES.min0Msg)
      .max(1000, MESSAGES.maxBeamMsg)
      .test(
        "parent starts at zero",
        MESSAGES.parentBeamStartsAtZero,
        function (value, context) {
          const index = parseInt(context.path.match(/\[(\d+)\]/)?.[1] || "0")
          const parentChildRelationships = getParentChildRelationships()

          // Check if this is a parent row (not in any parent's children list)
          const isChild = Object.values(parentChildRelationships).some(
            (childIndices) => childIndices.includes(index)
          )

          // If this is a parent row, it must start with minBeam = 0
          if (!isChild && parseInt(value || 0) !== 0) {
            return false
          }

          return true
        }
      )
      .test(
        "beam continuity within relationship",
        MESSAGES.beamContinuity,
        function (value, context) {
          // Use from[1].value.rates - this is the rateStrategy.rates array with current values
          const allRates = context.from?.[1]?.value?.rates || []

          const index = parseInt(context.path.match(/\[(\d+)\]/)?.[1] || "0")
          const parentChildRelationships = getParentChildRelationships()

          // Early returns for performance
          if (!allRates || allRates.length === 0 || index === 0) return true
          if (
            !parentChildRelationships ||
            Object.keys(parentChildRelationships).length === 0
          )
            return true

          if (!allRates || allRates.length === 0 || index === 0) return true

          // Find which parent this index belongs to
          let parentIndex = null
          let childIndices = []

          Object.entries(parentChildRelationships).forEach(
            ([parentIdx, children]) => {
              if (children.includes(index)) {
                parentIndex = parseInt(parentIdx)
                childIndices = children
              }
            }
          )

          // If this is a child row, validate continuity within the relationship
          if (parentIndex !== null) {
            // Sort all indices in this relationship (parent + children)
            const allIndicesInGroup = [parentIndex, ...childIndices].sort(
              (a, b) => a - b
            )
            const positionInGroup = allIndicesInGroup.indexOf(index)

            if (positionInGroup > 0) {
              const previousIndexInGroup =
                allIndicesInGroup[positionInGroup - 1]
              const previousRate = allRates[previousIndexInGroup]

              if (previousRate && previousRate.maxBeam !== undefined) {
                const expectedMinBeam = parseInt(previousRate.maxBeam || 0)
                const actualMinBeam = parseInt(value || 0)

                if (actualMinBeam !== expectedMinBeam) {
                  return false
                }
              }
            }
          }

          return true
        }
      ),
    maxBeam: yup
      .number()
      .typeError(MESSAGES.numberMsg)
      .required(MESSAGES.required)
      .min(0, MESSAGES.min0Msg)
      .max(1000, MESSAGES.maxBeamMsg)
      .when("minBeam", (minBeam) => {
        if (minBeam) {
          return yup
            .number()
            .typeError(MESSAGES.numberMsg)
            .min(minBeam, MESSAGES.maxBeamGreaterThanMin)
            .max(1000, MESSAGES.maxBeamMsg)
        }
      }),
    rateTypeOverride: yup.string().optional().nullable(),
  }

  const ratesValidation = yup
    .array()
    .of(yup.object().shape(baseRateShape).required(MESSAGES.required))
    .min(1, MESSAGES.atLeastOneRateRequired)
    .required(MESSAGES.required)
    .test(
      "at least one minLength zero",
      MESSAGES.atLeastOneMinLengthZero,
      (rates) => rates.some((rate) => rate.minLength === 0)
    )
    .test(
      "parent beams start at zero",
      MESSAGES.allParentBeamsStartAtZero,
      (rates) => {
        const parentChildRelationships = getParentChildRelationships()

        // Get all parent indices (those not in any children list)
        const allChildIndices = new Set()
        Object.values(parentChildRelationships).forEach((children) => {
          children.forEach((childIndex) => allChildIndices.add(childIndex))
        })

        // Check that all parent rows have minBeam = 0
        return rates.every((rate, index) => {
          const isChild = allChildIndices.has(index)
          if (!isChild) {
            // This is a parent row, it must have minBeam = 0
            return parseInt(rate.minBeam || 0) === 0
          }
          return true
        })
      }
    )

  return yup
    .object()
    .shape({
      rateThresholds: yup.array().of(
        yup.array().of(
          yup.object().shape({
            rate: yup
              .number()
              .typeError(MESSAGES.numberMsg)
              .required(MESSAGES.required)
              .min(0, MESSAGES.min0Msg),
          })
        )
      ),
      thresholds: yup
        .array()
        .of(
          yup
            .number()
            .typeError(MESSAGES.numberMsg)
            .required(MESSAGES.required)
            .min(0, MESSAGES.min0Msg)
        ),
      rateStrategy: yup.object().shape({
        name: yup
          .string()
          .typeError(MESSAGES.nameMsg)
          .required(MESSAGES.required),
        rateType: yup
          .string()
          .typeError(MESSAGES.rateTypeMsg)
          .required(MESSAGES.required),
        advancedPricing: yup.boolean().required(MESSAGES.required),
        rates: ratesValidation,
      }),
    })
    .required()
}

export default schema
